<script lang="ts">
	import { onMount } from 'svelte';
	import type { BrandConfig } from '$lib/brand/types';

	interface Props {
		config: BrandConfig;
		className?: string;
	}

	let { config, className = '' }: Props = $props();

	// Extract form details from config
	let formHtml = $state(config.convertkit?.formHtml);
	const formAction = config.convertkit?.formAction;
	const formUid = config.convertkit?.formUid;
	const formId = config.convertkit?.formId;

	// Load ConvertKit script and form HTML
	onMount(async () => {
		if (typeof window !== 'undefined') {
			// Load ConvertKit script if not already loaded
			if (!document.querySelector('script[src*="f.convertkit.com"]')) {
				const script = document.createElement('script');
				script.src = 'https://f.convertkit.com/ckjs/ck.5.js';
				script.async = true;
				document.head.appendChild(script);
			}

			// Load form HTML from file if formId exists (indicating this brand should have a ConvertKit form)
			if (formId) {
				try {
					const response = await fetch(`/brands/${config.brand}/convertkit-form.html`);
					if (response.ok) {
						const html = await response.text();
						// Update the form HTML in the component
						formHtml = html;
					} else {
						console.warn(`ConvertKit form HTML not found for brand: ${config.brand}`);
					}
				} catch (error) {
					console.warn('Could not load ConvertKit form HTML:', error);
				}
			}
		}
	});
</script>

{#if formHtml}
	{@html formHtml}
{:else}
	<!-- Fallback form if no ConvertKit HTML is provided -->
	<div class="form-container">
		<div class="fallback-form {className}" data-form-id={config.convertkit?.formId}>
			<div class="form-placeholder">
				<h3>Sign Up for {config.displayName}</h3>
				<p>Enter your email below to get started:</p>

				<form class="convertkit-form" action="/thank-you/" method="get">
					<div class="form-group">
						<input
							type="email"
							name="email"
							placeholder="Enter your email address"
							required
							class="email-input"
						/>
					</div>
					<div class="form-group">
						<input
							type="hidden"
							name="brand"
							value={config.brand}
						/>
						<input
							type="hidden"
							name="variant"
							value="landing_page"
						/>
						<button type="submit" class="submit-button">
							Get Started Now
						</button>
					</div>
				</form>

				<p class="form-disclaimer">
					We respect your privacy. Unsubscribe at any time.
				</p>
			</div>
		</div>
	</div>
{/if}

<style>

	.form-container {
		background: rgba(255, 255, 255, 0.1);
		backdrop-filter: blur(10px);
		border-radius: 16px;
		padding: 2.5rem;
		margin-bottom: 3rem;
		border: 1px solid rgba(255, 255, 255, 0.2);
	}

	@media (max-width: 768px) {
		.form-container {
		padding: 2rem 1.5rem;
		}
	}

	.form-header h3 {
		font-size: 1.5rem;
		margin-bottom: 1rem;
		color: white;
		text-align: center;
		font-weight: 600;
	}

	.form-header p {
		margin-bottom: 1.5rem;
		opacity: 0.9;
		color: white;
		text-align: center;
	}

	.fallback-form {
		width: 100%;
		max-width: 100%;
	}

	.form-placeholder h3 {
		font-size: 1.5rem;
		margin-bottom: 1rem;
		color: white;
		text-align: center;
		font-weight: 600;
	}

	.form-placeholder p {
		margin-bottom: 1.5rem;
		opacity: 0.9;
		color: white;
		text-align: center;
	}

	.form-group {
		margin-bottom: 1rem;
	}

	.email-input {
		width: 100%;
		padding: 1rem;
		border: 2px solid rgba(255, 255, 255, 0.3);
		border-radius: 8px;
		background: rgba(255, 255, 255, 0.1);
		color: white;
		font-size: 1rem;
		backdrop-filter: blur(5px);
		box-sizing: border-box;
	}

	.email-input::placeholder {
		color: rgba(255, 255, 255, 0.7);
	}

	.email-input:focus {
		outline: none;
		border-color: white;
		background: rgba(255, 255, 255, 0.2);
		box-shadow: none;
	}

	.submit-button {
		width: 100%;
		padding: 1rem 2rem;
		background: white;
		color: var(--brand-primary, #1e293b);
		border: none;
		border-radius: 8px;
		font-size: 1.1rem;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
	}

	.submit-button:hover {
		background: rgba(255, 255, 255, 0.9);
		transform: translateY(-2px);
	}

	.fallback-form .form-disclaimer {
		font-size: 0.9rem;
		opacity: 0.8;
		margin-top: 1rem;
		color: white;
		text-align: center;
	}

	/* Override ConvertKit form styles to match glassmorphism design */
	:global(.formkit-form [data-style="clean"]) {
		background: transparent !important;
		padding: 0 !important;
	}
	:global(.formkit-form .formkit-alert:empty) {
		display: none !important;
	}
	:global(.formkit-submit:hover) {
		background: rgba(255, 255, 255, 0.9) !important;
		transform: translateY(-2px) !important;
	}
	:global(.formkit-powered-by-convertkit-container) {
		display: none !important;
	}
	:global(ul.formkit-alert) {
		display: none !important;
		margin: 0 !important;
		padding: 0 !important;
		margin-block-start: 0 !important;
		margin-block-end: 0 !important;
		padding-block-start: 0 !important;
		padding-block-end: 0 !important;
		padding-inline-start: 0 !important;
		padding-inline-end: 0 !important;
		margin-inline-start: 0 !important;
		margin-inline-end: 0 !important;
	}
</style>
